/**
 * Landed Cost Engine — Pure utility for Import Purchase Invoice (IPI).
 *
 * Takes line items + charges + exchange rate, returns each line's landed unit cost
 * (foreign cost in PKR + allocated capitalized charges) plus the accounting totals
 * for non-capitalized charges (GST Input, WHT Receivable, etc.).
 *
 * No Firestore, no React, no side effects. 100% testable in isolation.
 *
 * ─────────────── Input ───────────────
 *
 * line = {
 *   itemId, itemCode, itemName,
 *   quantity,                 // number, > 0
 *   unitPriceForeign,         // number, in foreign currency
 *   discountAmountForeign,    // number, optional, foreign currency
 *   weight,                   // number, optional — required if any charge uses 'weight' allocation
 *   volume,                   // number, optional — required if any charge uses 'volume' allocation
 * }
 *
 * charge = {
 *   chargeName,               // 'Customs Duty'
 *   type,                     // 'percentage' | 'amount'
 *   value,                    // % rate (10) or PKR amount (70000)
 *   base,                     // 'cif' | 'cifPlusDuties' | 'manual'  (only used when type=percentage)
 *   accountId, accountCode, accountName,                  // Cr (payable) — always required
 *   claimAccountId, claimAccountCode, claimAccountName,   // Dr — required only when capitalize=false
 *   capitalize,               // boolean — true = adds to inventory cost, false = posts to its own claim account
 *   allocationMethod,         // 'value' | 'qty' | 'weight' | 'volume' | 'manual'
 *   manualAllocations         // optional: { [lineIndex]: amount } when allocationMethod='manual'
 * }
 *
 * exchangeRate = number (foreign → base, e.g. 1 USD = 280 PKR → rate = 280)
 *
 * ─────────────── Output ───────────────
 *
 * { lines: enriched[], charges: enriched[], summary, errors[], isValid }
 *
 * Each enriched line gets:
 *   foreignTotal, baseTotal, allocatedCharges[], capitalizedTotal, nonCapitalizedTotal,
 *   landedTotal (= baseTotal + capitalizedTotal),
 *   landedUnitCost (= landedTotal / quantity)  ← stamped onto stockFlow
 *
 * Each enriched charge gets:
 *   chargeAmount, allocations[] (per line), isValid, error
 *
 * summary = {
 *   foreignSubtotal, exchangeRate, baseSubtotal,
 *   capitalizedTotal, nonCapitalizedTotal,
 *   inventoryValue,        // baseSubtotal + capitalizedTotal — what hits Inventory Control (Dr)
 *   grandTotalOwed         // baseSubtotal + capitalizedTotal + nonCapitalizedTotal — sum of all credits
 * }
 */

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

const safeNum = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
};

const round2 = (n) => Math.round(safeNum(n) * 100) / 100;

// ────────────────────────────────────────────────────────────────────
// Per-line foreign / base totals (exposed for UI display)
// ────────────────────────────────────────────────────────────────────

export const calculateLineForeignTotal = (line) => {
  const gross = safeNum(line?.unitPriceForeign) * safeNum(line?.quantity);
  const discount = safeNum(line?.discountAmountForeign);
  return Math.max(0, gross - discount);
};

export const calculateLineBaseTotal = (line, exchangeRate) => {
  return calculateLineForeignTotal(line) * safeNum(exchangeRate);
};

// ────────────────────────────────────────────────────────────────────
// Charge amount computation
// Resolves the actual PKR amount of a charge based on its type and base.
// ────────────────────────────────────────────────────────────────────

const computeChargeAmount = (charge, baseTotalSum, prevChargeTotalsSum) => {
  if (!charge) return 0;
  const value = safeNum(charge.value);

  // Fixed amount — direct, no base needed
  if (charge.type === 'amount') return value;

  // Percentage — needs a base
  let base = 0;
  switch (charge.base) {
    case 'cif':
      base = baseTotalSum;
      break;
    case 'cifPlusDuties':
      base = baseTotalSum + prevChargeTotalsSum;
      break;
    case 'manual':
    default:
      // % with manual base doesn't make sense — return 0 and let the form warn
      base = 0;
      break;
  }
  return base * (value / 100);
};

// ────────────────────────────────────────────────────────────────────
// Allocation across lines
// Returns { allocations: number[] (one per line), error: string|null }
// Sum of allocations == chargeAmount (within ±1 paisa, last-line adjusted)
// ────────────────────────────────────────────────────────────────────

const allocateAcrossLines = (chargeAmount, charge, lines, lineBaseTotals) => {
  const n = lines.length;
  const result = new Array(n).fill(0);

  if (n === 0) return { allocations: result, error: null };
  if (round2(chargeAmount) === 0) return { allocations: result, error: null };

  // Manual allocation: user typed exact splits
  if (charge.allocationMethod === 'manual') {
    const allocs = charge.manualAllocations || {};
    for (let i = 0; i < n; i++) result[i] = round2(allocs[i]);
    const sum = round2(result.reduce((a, b) => a + b, 0));
    const diff = round2(chargeAmount - sum);
    if (Math.abs(diff) > 0.01) {
      return {
        allocations: result,
        error: `Manual allocation total (${sum.toFixed(2)}) does not match charge amount (${chargeAmount.toFixed(2)}). Diff: ${diff.toFixed(2)}.`
      };
    }
    return { allocations: result, error: null };
  }

  // Pick the weight basis
  let weights;
  let basisLabel;
  switch (charge.allocationMethod) {
    case 'value':
      weights = lineBaseTotals;
      basisLabel = 'value';
      break;
    case 'qty':
      weights = lines.map((l) => safeNum(l.quantity));
      basisLabel = 'quantity';
      break;
    case 'weight':
      weights = lines.map((l) => safeNum(l.weight));
      basisLabel = 'weight';
      break;
    case 'volume':
      weights = lines.map((l) => safeNum(l.volume));
      basisLabel = 'volume';
      break;
    default:
      weights = lines.map(() => 1); // equal split fallback
      basisLabel = 'equal';
  }

  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight === 0) {
    return {
      allocations: result,
      error: `Cannot allocate "${charge.chargeName || 'charge'}" by ${basisLabel}: total ${basisLabel} across all lines is zero. Enter a ${basisLabel} on each line or pick a different allocation method.`
    };
  }

  // Find the line with the largest weight — it absorbs any rounding remainder
  let largestIndex = 0;
  for (let i = 1; i < n; i++) {
    if (weights[i] > weights[largestIndex]) largestIndex = i;
  }

  // Initial proportional allocation (rounded to 2 decimals)
  for (let i = 0; i < n; i++) {
    result[i] = round2(chargeAmount * (weights[i] / totalWeight));
  }

  // Reconcile rounding drift: adjust the largest-weighted line so Σ == chargeAmount
  const sum = result.reduce((a, b) => a + b, 0);
  const diff = round2(chargeAmount - sum);
  if (diff !== 0) {
    result[largestIndex] = round2(result[largestIndex] + diff);
  }

  return { allocations: result, error: null };
};

// ────────────────────────────────────────────────────────────────────
// Main orchestrator
// ────────────────────────────────────────────────────────────────────

export const computeLandedCost = ({ lines = [], charges = [], exchangeRate = 1 } = {}) => {
  const rate = safeNum(exchangeRate) || 1;

  // Step 1: per-line foreign and base totals
  const lineForeignTotals = lines.map(calculateLineForeignTotal);
  const lineBaseTotals = lines.map((l) => calculateLineBaseTotal(l, rate));
  const baseTotalSum = lineBaseTotals.reduce((a, b) => a + b, 0);
  const foreignSubtotal = lineForeignTotals.reduce((a, b) => a + b, 0);

  // Step 2: walk charges in order, computing amount + allocating
  // (Order matters because 'cifPlusDuties' bases reference all prior charges.)
  const errors = [];
  let cumulativeChargeTotals = 0;
  const lineAllocated = lines.map(() => []); // per-line list of {chargeName, accountId, amount, capitalize}

  const chargeResults = charges.map((charge, ci) => {
    const chargeAmount = round2(
      computeChargeAmount(charge, baseTotalSum, cumulativeChargeTotals)
    );
    const { allocations, error } = allocateAcrossLines(
      chargeAmount,
      charge,
      lines,
      lineBaseTotals
    );
    if (error) errors.push(error);

    allocations.forEach((amt, li) => {
      lineAllocated[li].push({
        chargeName: charge.chargeName,
        accountId: charge.accountId,
        accountCode: charge.accountCode,
        accountName: charge.accountName,
        claimAccountId: charge.claimAccountId,
        claimAccountCode: charge.claimAccountCode,
        claimAccountName: charge.claimAccountName,
        amount: amt,
        capitalize: !!charge.capitalize,
      });
    });

    cumulativeChargeTotals += chargeAmount;

    return {
      chargeName: charge.chargeName,
      type: charge.type,
      value: safeNum(charge.value),
      base: charge.base,
      accountId: charge.accountId,
      accountCode: charge.accountCode,
      accountName: charge.accountName,
      claimAccountId: charge.claimAccountId,
      claimAccountCode: charge.claimAccountCode,
      claimAccountName: charge.claimAccountName,
      capitalize: !!charge.capitalize,
      allocationMethod: charge.allocationMethod,
      chargeAmount,
      allocations,
      isValid: !error,
      error: error || null,
    };
  });

  // Step 3: enrich each line with allocation totals + landed cost
  const enrichedLines = lines.map((line, i) => {
    const allocated = lineAllocated[i];
    const capitalizedTotal = round2(
      allocated.filter((a) => a.capitalize).reduce((s, a) => s + a.amount, 0)
    );
    const nonCapitalizedTotal = round2(
      allocated.filter((a) => !a.capitalize).reduce((s, a) => s + a.amount, 0)
    );
    const baseTotal = round2(lineBaseTotals[i]);
    const landedTotal = round2(baseTotal + capitalizedTotal);
    const qty = safeNum(line.quantity);
    const landedUnitCost = qty > 0 ? round2(landedTotal / qty) : 0;

    return {
      ...line,
      foreignTotal: round2(lineForeignTotals[i]),
      baseTotal,
      allocatedCharges: allocated,
      capitalizedTotal,
      nonCapitalizedTotal,
      landedTotal,
      landedUnitCost,
    };
  });

  // Step 4: summary totals
  const totalCapitalized = round2(
    chargeResults.filter((c) => c.capitalize).reduce((s, c) => s + c.chargeAmount, 0)
  );
  const totalNonCapitalized = round2(
    chargeResults.filter((c) => !c.capitalize).reduce((s, c) => s + c.chargeAmount, 0)
  );

  const summary = {
    foreignSubtotal: round2(foreignSubtotal),
    exchangeRate: rate,
    baseSubtotal: round2(baseTotalSum),
    capitalizedTotal: totalCapitalized,
    nonCapitalizedTotal: totalNonCapitalized,
    inventoryValue: round2(baseTotalSum + totalCapitalized),
    grandTotalOwed: round2(baseTotalSum + totalCapitalized + totalNonCapitalized),
  };

  return {
    lines: enrichedLines,
    charges: chargeResults,
    summary,
    errors,
    isValid: errors.length === 0,
  };
};

// ────────────────────────────────────────────────────────────────────
// Accounting helpers — used by subTransactionHelper in Phase 4
// to fan out IPI's multi-credit accounting entries.
// ────────────────────────────────────────────────────────────────────

/**
 * Returns charges that should be capitalized into inventory.
 * These do NOT post to their own account; they roll into Inventory Control
 * via the per-line landedUnitCost. The "credit" side still posts to the
 * charge's account (e.g., Customs Payable, Freight Payable).
 */
export const getCapitalizedCharges = (landedResult) =>
  (landedResult?.charges || []).filter((c) => c.capitalize && c.chargeAmount > 0);

/**
 * Returns charges that are claimable / expense (not part of inventory cost).
 * These post directly: Dr <charge.accountId> for chargeAmount.
 * Examples: GST Input (claimable Sales Tax), WHT Receivable.
 */
export const getNonCapitalizedCharges = (landedResult) =>
  (landedResult?.charges || []).filter((c) => !c.capitalize && c.chargeAmount > 0);

const landedCostHelper = {
  computeLandedCost,
  calculateLineForeignTotal,
  calculateLineBaseTotal,
  getCapitalizedCharges,
  getNonCapitalizedCharges,
};

export default landedCostHelper;
